import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { fmtUSD } from "@/lib/helpers";
import {
  BtnPrimary, BtnSecondary, BtnGhost, Modal, Field, Input, Select, Empty,
} from "@/components/primitives";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Package } from "@phosphor-icons/react";

const BLANK = { code: "", name: "", rate_usd: 0, active: true };

export default function CrushProductsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(BLANK);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/production/products");
      setRows(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openNew() { setEdit(null); setForm(BLANK); setOpen(true); }
  function openEdit(r) {
    setEdit(r);
    setForm({ code: r.code, name: r.name, rate_usd: r.rate_usd, active: r.active !== false });
    setOpen(true);
  }

  async function save() {
    if (!form.code || !form.name) return toast.error("Code and name are required");
    try {
      await api.post("/production/products", {
        code: form.code.toUpperCase().trim(),
        name: form.name.trim(),
        rate_usd: Number(form.rate_usd || 0),
        active: form.active !== false,
      });
      toast.success(edit ? "Product updated" : "Product created");
      setOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function remove(r) {
    if (!window.confirm(`Delete product "${r.code}" (${r.name})?`)) return;
    try {
      await api.delete(`/production/products/${encodeURIComponent(r.code)}`);
      toast.success("Product removed");
      load();
    } catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="crush-products-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-[#52525b]">
          {rows.length} product{rows.length === 1 ? "" : "s"} · used across Production, DNs and Invoices
        </div>
        <BtnPrimary onClick={openNew} data-testid="crush-product-new">
          <Plus size={12} weight="bold" className="inline mr-1.5" /> New Product
        </BtnPrimary>
      </div>

      {loading ? (
        <div className="phizo-label">Loading…</div>
      ) : rows.length === 0 ? (
        <Empty label="No products yet. Add your first size to enable production logging." />
      ) : (
        <div className="border border-[#e4e4e7]">
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr>
                  <th style={{ width: "18%" }}>Code</th>
                  <th>Name / Size</th>
                  <th className="text-right">Rate (USD / t)</th>
                  <th className="text-center">Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id || r.code} data-testid={`crush-product-row-${r.code}`}>
                    <td className="mono font-bold">
                      <div className="flex items-center gap-2">
                        <Package size={14} weight="bold" className="text-[#00A142]" />
                        {r.code}
                      </div>
                    </td>
                    <td>{r.name}</td>
                    <td className="text-right mono">{fmtUSD(r.rate_usd)}</td>
                    <td className="text-center">
                      {r.active !== false ? (
                        <span className="phizo-status" style={{ background: "#00C853", color: "#09090b", borderColor: "#00C853" }}>Active</span>
                      ) : (
                        <span className="phizo-status" style={{ background: "#a1a1aa", color: "#fff", borderColor: "#a1a1aa" }}>Archived</span>
                      )}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <BtnGhost onClick={() => openEdit(r)}
                        data-testid={`crush-product-edit-${r.code}`}>
                        <PencilSimple size={12} weight="bold" />
                      </BtnGhost>
                      <BtnGhost onClick={() => remove(r)} className="text-[#FF2A2A]"
                        data-testid={`crush-product-delete-${r.code}`}>
                        <Trash size={12} weight="bold" />
                      </BtnGhost>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)}
        title={edit ? `Edit ${edit.code}` : "New Crush Product"} testId="crush-product-modal">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Product Code *" hint="Short unique identifier — used in daily logs & DNs. e.g. 0-7, 6-14, STEMMING.">
            <Input
              value={form.code}
              disabled={!!edit}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              data-testid="crush-product-code"
              placeholder="0-7" />
          </Field>
          <Field label="Size / Name *">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="crush-product-name"
              placeholder="0-7mm Road Base" />
          </Field>
          <Field label="Rate (USD per tonne) *">
            <Input
              type="number" step="0.01" min="0"
              value={form.rate_usd}
              onChange={(e) => setForm({ ...form, rate_usd: Number(e.target.value) })}
              data-testid="crush-product-rate" />
          </Field>
          <Field label="Status">
            <Select value={form.active ? "yes" : "no"}
              onChange={(e) => setForm({ ...form, active: e.target.value === "yes" })}
              data-testid="crush-product-status">
              <option value="yes">Active</option>
              <option value="no">Archived</option>
            </Select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-[#e4e4e7] mt-4">
          <BtnSecondary onClick={() => setOpen(false)}>Cancel</BtnSecondary>
          <BtnPrimary onClick={save} data-testid="crush-product-save">
            {edit ? "Save Changes" : "Create Product"}
          </BtnPrimary>
        </div>
      </Modal>
    </div>
  );
}
